import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatGel } from '../lib/format';
import { useI18n, useLocalized } from '../lib/i18n';
import { usePayroll, usePayrollJobs, type PayrollPerson } from '../lib/queries';
import type { StringKey } from '../lib/strings';
import { Amount, Card, EmptyState, Pill, Skeleton } from '../components/ui';

const TIMEZONE = 'Asia/Tbilisi';

/** A studio date as the API takes it, YYYY-MM-DD. */
const studioDate = (at: Date) => new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIMEZONE,
}).format(at);

/** Day arithmetic on YYYY-MM-DD, done in UTC so no clock change can shift it. */
const shift = (day: string, days: number) => {
  const at = new Date(`${day}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};

type Preset = 'today' | 'week' | 'month' | 'lastMonth' | 'custom';

function rangeOf(preset: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const today = studioDate(new Date());
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week') {
    const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
    return { from: shift(today, -weekday), to: today };
  }
  const monthStart = `${today.slice(0, 8)}01`;
  if (preset === 'month') return { from: monthStart, to: today };
  const lastEnd = shift(monthStart, -1);
  return { from: `${lastEnd.slice(0, 8)}01`, to: lastEnd };
}

const PRESETS: Array<{ id: Preset; label: StringKey }> = [
  { id: 'today', label: 'payroll.today' },
  { id: 'week', label: 'payroll.week' },
  { id: 'month', label: 'payroll.month' },
  { id: 'lastMonth', label: 'payroll.lastMonth' },
  { id: 'custom', label: 'payroll.custom' },
];

const dateInput = 'tabular w-full min-w-0 rounded-xl border border-line bg-raised px-3 py-2.5 text-ink focus:border-brand focus:outline-none';

/** One person's jobs over the period, each with what it pays. */
function PersonJobs({ adminUserId, from, to }: { adminUserId: string; from: string; to: string }) {
  const { t } = useI18n();
  const localized = useLocalized();
  const { data, isPending } = usePayrollJobs(adminUserId, from, to);

  if (isPending) return <Skeleton className="mt-3 h-20" />;
  const items = data?.items ?? [];
  if (items.length === 0) return <p className="mt-3 text-sm text-ink-dim">{t('reports.noWork')}</p>;

  return (
    <div className="mt-3 divide-y divide-line/60 rounded-xl border border-line bg-raised/40">
      {items.map((job) => (
        <div key={job.itemId} className="flex items-start justify-between gap-3 px-3 py-2.5">
          <div className="min-w-0">
            <div className="truncate text-sm text-ink">
              {localized(job.service)}
              {job.quantity > 1 && <span className="text-ink-faint"> × {job.quantity}</span>}
            </div>
            <div className="tabular mt-0.5 flex flex-wrap gap-x-2 text-[0.7rem] text-ink-faint">
              <span>{formatDateTime(job.completedAt)}</span>
              {job.car && <span>{job.car}</span>}
              <Link to={`/orders/${job.orderId}`} className="text-brand hover:underline">{job.reference}</Link>
              {job.refundedQuantity > 0 && <span className="text-warning">{t('order.refundedQty', { count: job.refundedQuantity })}</span>}
            </div>
          </div>
          {job.payTetri === null
            ? <span className="shrink-0 text-xs text-warning">{t('payroll.noRate')}</span>
            : <Amount value={formatGel(job.payTetri)} className="shrink-0 text-sm text-ink" />}
        </div>
      ))}
    </div>
  );
}

function PersonRow({ person, open, onToggle, from, to }: {
  person: PayrollPerson; open: boolean; onToggle: () => void; from: string; to: string;
}) {
  const { t } = useI18n();
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <button onClick={onToggle} className="flex w-full items-start justify-between gap-3 text-left">
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-ink">
            {person.name}
            {!person.isActive && <span className="ml-2 align-middle"><Pill>{t('staff.inactive')}</Pill></span>}
          </span>
          {person.jobTitle && <span className="block truncate text-[0.7rem] text-ink-faint">{person.jobTitle}</span>}
          <span className="tabular mt-1 flex flex-wrap gap-x-3 text-[0.7rem] text-ink-dim">
            <span>{t('payroll.today')} {formatGel(person.todayTetri)}</span>
            <span>{t('payroll.week')} {formatGel(person.weekTetri)}</span>
            <span>{t('payroll.month')} {formatGel(person.monthTetri)}</span>
          </span>
        </span>
        <span className="shrink-0 text-right">
          <Amount value={formatGel(person.periodTetri)} className="block text-base font-semibold text-ink" />
          <span className="block text-[0.65rem] text-ink-faint">
            {t('reports.jobsAndCars', { jobs: person.jobs, cars: person.cars })}
          </span>
          {person.unpriced > 0 && (
            <span className="mt-1 inline-block"><Pill tone="warning">{t('payroll.unpriced', { count: person.unpriced })}</Pill></span>
          )}
        </span>
      </button>
      {open && <PersonJobs adminUserId={person.adminUserId} from={from} to={to} />}
    </div>
  );
}

/** What each member of staff is owed: today, this week, this month, and over any period with the jobs behind it. */
export function PayrollPage() {
  const { t } = useI18n();
  const [preset, setPreset] = useState<Preset>('month');
  const [custom, setCustom] = useState(() => rangeOf('month'));
  const [openPerson, setOpenPerson] = useState<string | null>(null);
  const { from, to } = preset === 'custom' ? custom : rangeOf(preset);
  const { data, isPending } = usePayroll(from, to);
  const today = studioDate(new Date());

  const people = (data?.people ?? []).filter((p) => p.jobs > 0 || p.monthTetri > 0 || p.todayTetri > 0);

  return (
    <div className="mx-auto max-w-3xl">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('payroll.title')}</h1>
      </header>

      <div className="-mx-4 mb-3 flex gap-1.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
        {PRESETS.map((option) => (
          <button
            key={option.id}
            onClick={() => { setPreset(option.id); setOpenPerson(null); }}
            className={`inline-flex min-h-10 shrink-0 items-center rounded-lg border px-3 text-sm font-medium transition ${
              preset === option.id ? 'border-brand bg-brand-wash text-brand' : 'border-line bg-raised text-ink-dim hover:text-ink'
            }`}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('reports.from')}</span>
            <input type="date" value={custom.from} max={custom.to} className={dateInput}
              onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))} />
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('reports.to')}</span>
            <input type="date" value={custom.to} min={custom.from} max={today} className={dateInput}
              onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))} />
          </label>
        </div>
      )}

      {isPending && <Skeleton className="h-48" />}

      {data && (
        <>
          <Card className="mb-3">
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('payroll.total')}</div>
                <Amount value={formatGel(data.totals.periodTetri)} className="mt-1 block font-display text-3xl font-bold text-ink" />
              </div>
              <div className="text-right text-xs text-ink-dim">
                <div>{t('payroll.jobs', { count: data.totals.jobs })}</div>
                {data.totals.unpriced > 0 && (
                  <div className="mt-1"><Pill tone="warning">{t('payroll.unpriced', { count: data.totals.unpriced })}</Pill></div>
                )}
              </div>
            </div>
          </Card>

          {people.length === 0
            ? <EmptyState title={t('reports.noWork')} />
            : (
              <Card>
                <div className="divide-y divide-line/60">
                  {people.map((person) => (
                    <PersonRow
                      key={person.adminUserId}
                      person={person}
                      from={from}
                      to={to}
                      open={openPerson === person.adminUserId}
                      onToggle={() => setOpenPerson((was) => (was === person.adminUserId ? null : person.adminUserId))}
                    />
                  ))}
                </div>
              </Card>
            )}
        </>
      )}
    </div>
  );
}
